import React, { useRef, useState } from 'react';
import { Box, Typography, IconButton, Button, styled } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

const AnalysisContentWrapper = styled(Box)({
    backgroundColor: 'white',
    padding: '20px',
    borderRadius: '10px',
    maxWidth: '80%',
    maxHeight: '80%',
    overflow: 'auto',
    position: 'relative',
    color: 'black',
});

const CloseButton = styled(IconButton)({
    position: 'absolute',
    right: '10px',
    top: '10px',
    color: 'black',
});

const ButtonContainer = styled(Box)({
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: '20px',
    gap: '10px',
});

interface AnalysisContentProps {
    onClose: () => void;
    playerTurnHistory: any[];
    winners: any[];
    nonWinners: any[] | null;
}

class AnalysisContentClass {
    static readonly ANALYSIS_PROMPT = "I have Texas Hold'em Poker game data representing a full game with No-Limit betting rules. The playerTurnHistory shows player turns in order, with the game state at each turn. Winners and nonWinners represent the players who won and lost the game, respectively, along with their won amounts. Please analyze the following: 1) Is the game played correctly according to Texas Hold'em rules?. 2) Are the players' actions valid for each turn?. 3) Are the available actions correct for each turn?. 4) Are the balances accurate throughout the game?. 5) Have prizes been distributed correctly? (Check winners and nonWinners data). The goal is to verify adherence to Texas Hold'em rules, not to evaluate the quality of players' decisions. Please provide a concise analysis focusing on these points.";
}

const AnalysisContent: React.FC<AnalysisContentProps> = ({ onClose, playerTurnHistory, winners, nonWinners }) => {
    const [copyStatus, setCopyStatus] = useState<'idle' | 'success' | 'error'>('idle');
    const textAreaRef = useRef<HTMLTextAreaElement>(null);

    const filterKeys = (obj: any): any => {
        if (Array.isArray(obj)) {
            return obj.map(filterKeys);
        } else if (typeof obj === 'object' && obj !== null) {
            return Object.fromEntries(
                Object.entries(obj)
                    .filter(([key]) => !['userId', 'dealerId', 'smallBlindId', 'bigBlindId'].includes(key))
                    .map(([key, value]) => [key, filterKeys(value)])
            );
        }
        return obj;
    };

    const handleCopyAnalysis = () => {
        const filteredPlayerTurnHistory = filterKeys(playerTurnHistory);
        const filteredWinners = filterKeys(winners);
        const filteredNonWinners = filterKeys(nonWinners || []);

        const analysisData = {
            prompt: AnalysisContentClass.ANALYSIS_PROMPT,
            playerTurnHistory: JSON.stringify(filteredPlayerTurnHistory),
            winners: JSON.stringify(filteredWinners),
            nonWinners: JSON.stringify(filteredNonWinners)
        };
        const analysisText = JSON.stringify(analysisData);
        
        if (textAreaRef.current) {
            textAreaRef.current.value = analysisText;
            textAreaRef.current.select();
            try {
                document.execCommand('copy');
                setCopyStatus('success');
            } catch (err) {
                console.error('Failed to copy: ', err);
                setCopyStatus('error');
                alert('Failed to copy to clipboard. Please try again.');
            }
            textAreaRef.current.blur();
        }
    };

    const formattedAnalysisData = {
        prompt: AnalysisContentClass.ANALYSIS_PROMPT,
        playerTurnHistory: filterKeys(playerTurnHistory),
        winners: filterKeys(winners),
        nonWinners: filterKeys(nonWinners || [])
    };

    return (
        <AnalysisContentWrapper>
            <CloseButton onClick={onClose}>
                <CloseIcon />
            </CloseButton>
            <Typography variant="h6" gutterBottom>Game Analysis</Typography>
            <Typography paragraph>{AnalysisContentClass.ANALYSIS_PROMPT}</Typography>
            <Typography component="pre" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: '400px', overflow: 'auto' }}>
                {JSON.stringify(formattedAnalysisData, null, 2)}
            </Typography>
            <ButtonContainer>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<ContentCopyIcon />}
                    onClick={handleCopyAnalysis}
                >
                    {copyStatus === 'idle' && 'Copy to Clipboard'}
                    {copyStatus === 'success' && 'Copied!'}
                    {copyStatus === 'error' && 'Failed to Copy'}
                </Button>
                <Button
                    variant="outlined"
                    color="secondary"
                    onClick={onClose}
                >
                    Close
                </Button>
            </ButtonContainer>
            <textarea
                ref={textAreaRef}
                style={{ position: 'absolute', left: '-9999px' }}
                aria-hidden="true"
            />
        </AnalysisContentWrapper>
    );
};

export default AnalysisContent;
